import type { BlockType } from '@unifyapps/defs/types/block';
import type { InterfaceFolder } from '@unifyapps/defs/types/interface';
import type { JavaScriptFunction } from '@unifyapps/defs/types/javascriptFunction';
import type { JavaScriptVariable } from '@unifyapps/defs/types/javascriptVariable';
import type { WalkthroughStepType } from '@unifyapps/defs/types/page/walkthrough';
import DataSourceHelper from '../../helper/DataSourceHelper';
import type { UnsavedDataSource } from '../../stores/DataSourceRecordStore';
import { isReservedWord } from './reservedWords';
import type { EntityType, ValidationResult } from './types';

const ENTITY_TYPE_NAMES = {
  pageVariable: 'variable',
  pageFunction: 'function',
  block: 'block',
  dataSource: 'data source',
  folder: 'folder',
  walkthroughStep: 'walkthrough step',
} as const;

type EntityNamesDict = Record<EntityType, Map<string, string>>;

export type ValidateEntityTypes = {
  dataSources: Record<string, UnsavedDataSource | undefined>;
  pageVariables: Record<string, JavaScriptVariable | undefined>;
  pageFunctions: Record<string, JavaScriptFunction | undefined>;
  blocks: Record<string, BlockType | undefined>;
  folders: Record<string, InterfaceFolder | undefined>;
  walkthroughSteps: Record<string, WalkthroughStepType | undefined>;
};
/**
 * Extracts entity names from the application context to build a dictionary for validation.
 * This dictionary maps lowercase entity names to their IDs for efficient lookup during validation.
 *
 * @param dataSources - Record of all available data sources
 * @param pageVariables - Record of all available page variables
 * @param pageFunctions - Record of all available page functions
 * @param blocks - Record of all available blocks
 * @param folders - Record of all available folders
 * @param walkthroughSteps - Record of all available walkthrough steps
 * @returns A dictionary of entity names organized by entity type
 */
export function buildEntityNamesDictionary(params: ValidateEntityTypes): EntityNamesDict {
  const { dataSources, pageVariables, pageFunctions, blocks, folders, walkthroughSteps } = params;

  const dict: EntityNamesDict = {
    pageVariable: new Map<string, string>(),
    pageFunction: new Map<string, string>(),
    block: new Map<string, string>(),
    dataSource: new Map<string, string>(),
    folder: new Map<string, string>(),
    walkthroughStep: new Map<string, string>(),
  };

  if (pageVariables) {
    Object.entries(pageVariables).forEach(([id, variable]) => {
      if (!variable) return;
      dict.pageVariable.set(variable.name.toLowerCase(), id);
    });
  }

  if (pageFunctions) {
    Object.entries(pageFunctions).forEach(([id, func]) => {
      if (!func) return;
      dict.pageFunction.set(func.name.toLowerCase(), id);
    });
  }

  if (blocks) {
    Object.entries(blocks).forEach(([id, block]) => {
      if (!block) return;
      if (block.displayName) dict.block.set(block.displayName.toLowerCase(), id);
    });
  }

  Object.entries(dataSources).forEach(([id, dataSource]) => {
    const name = DataSourceHelper.name(dataSource);
    if (name) dict.dataSource.set(name.toLowerCase(), id);
  });

  if (folders) {
    Object.entries(folders).forEach(([id, folder]) => {
      if (!folder) return;
      if (folder.name) dict.folder.set(folder.name.toLowerCase(), id);
    });
  }

  if (walkthroughSteps) {
    Object.entries(walkthroughSteps).forEach(([id, step]) => {
      if (!step) return;
      if (step.label) dict.walkthroughStep.set(step.label.toLowerCase(), id);
    });
  }

  return dict;
}

/**
 * Validates entity name format according to naming rules.
 * Ensures names start with a letter, contain only valid characters,
 * and aren't reserved words.
 *
 * @param name - The entity name to validate
 * @returns Validation result with error details if invalid
 */
export function validateNameFormat(name: string): ValidationResult {
  if (!/^[A-Za-z]/.test(name)) {
    return {
      isValid: false,
      error: {
        type: 'naming-format',
        message: 'Name must start with a letter',
      },
    };
  }

  if (name.length < 3) {
    return {
      isValid: false,
      error: {
        type: 'naming-format',
        message: 'Name must be at least 3 characters long',
      },
    };
  }

  if (!/^\w*$/.test(name)) {
    return {
      isValid: false,
      error: {
        type: 'naming-format',
        message: 'Name can only contain alphanumerical symbols and underscores (_)',
      },
    };
  }

  if (isReservedWord(name)) {
    return {
      isValid: false,
      error: {
        type: 'reserved-word',
        message: `"${name}" is a reserved word and cannot be used`,
      },
    };
  }

  return { isValid: true };
}

/**
 * Checks if the entity name is already used by another entity of the same type.
 * Accounts for the case where the entity is being edited (by comparing entityId).
 *
 * @param name - The entity name to check
 * @param type - The entity type being validated
 * @param namesDict - Dictionary of all entity names
 * @param entityId - Optional ID of the entity being edited
 * @returns Validation result with error details if duplicate found
 */
export function checkDuplicateInType(
  name: string,
  type: EntityType,
  namesDict: EntityNamesDict,
  entityId?: string,
): ValidationResult {
  const normalizedName = name.toLowerCase();
  const existingId = namesDict[type].get(normalizedName);

  if (!existingId || existingId === entityId) {
    return { isValid: true };
  }

  return {
    isValid: false,
    error: {
      type: 'duplicate-name',
      message: `A ${ENTITY_TYPE_NAMES[type]} with the name already exists`,
    },
  };
}

/**
 * Checks if the entity name collides with names from other entity types.
 * This prevents naming conflicts across different entity categories.
 *
 * @param name - The entity name to check
 * @param currentType - The entity type being validated
 * @param namesDict - Dictionary of all entity names
 * @returns Validation result with error details if collision found
 */
export function checkCollisionsAcrossTypes(
  name: string,
  currentType: EntityType,
  namesDict: EntityNamesDict,
): ValidationResult {
  const normalizedName = name.toLowerCase();
  const typesToCheck = Object.keys(namesDict).filter(
    (type) => type !== currentType,
  ) as EntityType[];

  for (const type of typesToCheck) {
    if (namesDict[type].has(normalizedName)) {
      return {
        isValid: false,
        error: {
          type: 'duplicate-name',
          message: `The name "${name}" is already used by another ${ENTITY_TYPE_NAMES[type]}`,
        },
      };
    }
  }

  return { isValid: true };
}

/**
 * Main validation function that performs comprehensive entity name validation.
 * Runs a series of validation checks in sequence, returning on first failure.
 *
 * @param name - The entity name to validate
 * @param type - The entity type being validated
 * @param context - The current interface store state
 * @param dataSources - Record of all available data sources
 * @param entityId - Optional ID of the entity being edited
 * @returns Validation result with error details if invalid
 */
export function validateEntityName({
  name,
  type,
  dataSources,
  pageVariables,
  pageFunctions,
  blocks,
  folders,
  walkthroughSteps,
  entityId,
}: {
  name: string;
  type: EntityType;
  // optional to check if the name is already used by another entity when creating a new entity
  entityId?: string;
} & ValidateEntityTypes): ValidationResult {
  const formatResult = validateNameFormat(name);
  if (!formatResult.isValid) {
    return formatResult;
  }

  const namesDict = buildEntityNamesDictionary({
    dataSources,
    pageVariables,
    pageFunctions,
    blocks,
    folders,
    walkthroughSteps,
  });

  const duplicateResult = checkDuplicateInType(name, type, namesDict, entityId);
  if (!duplicateResult.isValid) {
    return duplicateResult;
  }

  const collisionResult = checkCollisionsAcrossTypes(name, type, namesDict);
  if (!collisionResult.isValid) {
    return collisionResult;
  }

  return { isValid: true };
}
